import type { KnownTrackingLabels } from '@shared/tracking/TrackingLabels.ts';
import { GALLERY_LANDSCAPE, GALLERY_LANDSCAPE_TWO_COLUMN } from '@tilelist/domain/composition/BracketLayout.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { EXPERIMENT_ONEX_IDS, type ExperimentOnexId } from '@tilelist/domain/experiments/Experiments.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { isBrandTileExperimentEligible } from '@tilelist/domain/feature-visibility/BrandTile.ts';
import { extractComparisonId } from '@tilelist/domain/feature-visibility/ComparisonService.ts';
import {
    shouldShowTopInfos,
    isInExperiment as topInfoIsInExperiment,
} from '@tilelist/domain/feature-visibility/TopInfoVisibility.ts';

type ExperimentDefinition = {
    /*                                                                                            */
    experimentId: ExperimentOnexId;
    /*                                                                                                                                  */
    experimentShouldBeTracked: boolean;
    /*                                                                                                                                                  */
    featureLabels?: Partial<KnownTrackingLabels>;
};

/*                                                                                                                                                           */
const EXP_OVERRIDE_ID = 'noExperiment';

function getExperimentDefinitions(pageView: TileListPageLayout, onex: OnEx): ExperimentDefinition[] {
    const searchContext = pageView.context.searchContext;
    const layout = pageView.context.layout;
    const isLandscapeSegment =
        layout.imageAspectRatio === GALLERY_LANDSCAPE.imageAspectRatio ||
        layout.imageAspectRatio === GALLERY_LANDSCAPE_TWO_COLUMN.imageAspectRatio;

    return [
        {
            experimentId: EXPERIMENT_ONEX_IDS.ADDITIONAL_ASSORTMENTS_FOR_TOP_INFOS,
            experimentShouldBeTracked: topInfoIsInExperiment(searchContext, onex),
            featureLabels: { 'wk.san_TopInfoList': shouldShowTopInfos(searchContext, onex) },
        },
        {
            experimentId: EXPERIMENT_ONEX_IDS.COMPARISON_ADDITIONAL_CATEGORIES,
            experimentShouldBeTracked: extractComparisonId(searchContext, undefined, onex).whatIf,
        },
        {
            /*                                                               */
            experimentId: EXPERIMENT_ONEX_IDS.TEASER_VARIATION_BOOST,
            experimentShouldBeTracked: pageView.teaserVariationBoostTracked ?? false,
        },
        {
            /*                                                               */
            /*                                                                */
            /*                                           */
            /*                                                              */
            /*                                                         */
            /*                                            */
            experimentId: EXPERIMENT_ONEX_IDS.GALLERY_LANDSCAPE_TWO_COLUMN,
            experimentShouldBeTracked: isLandscapeSegment && onex.isExperimentActive('GALLERY_LANDSCAPE_TWO_COLUMN'),
        },
        {
            experimentId: EXPERIMENT_ONEX_IDS.BRAND_TILE,
            experimentShouldBeTracked:
                isBrandTileExperimentEligible(searchContext) && onex.isExperimentActive('BRAND_TILE'),
        },
    ];
}

export function collectFeatureTracking(
    pageView: TileListPageLayout,
    onex: OnEx,
): {
    experimentIds: string[];
    featureLabels: Partial<KnownTrackingLabels>;
} {
    const definitions = getExperimentDefinitions(pageView, onex);
    const trackedExperimentIds = definitions.filter((d) => d.experimentShouldBeTracked).map((d) => d.experimentId);

    return {
        experimentIds: trackedExperimentIds.length > 0 ? trackedExperimentIds : [EXP_OVERRIDE_ID],
        featureLabels: Object.assign({}, ...definitions.map((d) => d.featureLabels ?? {})),
    };
}
